import React, { useEffect } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { LLMKeyProvider } from './contexts/LLMKeyContext'
import { UserRoleProvider } from './contexts/UserRoleContext'
import { PageContentWidthProvider } from './contexts/PageContentWidthContext'
import { PlatformKeyStatusProvider } from './contexts/PlatformKeyStatusContext'
import { AuthScopedUsageQuotaProvider } from './contexts/UsageQuotaContext'
import AuthErrorBoundary from './features/auth/AuthErrorBoundary'
import PlatformKeyBannerConnector from './features/platform/PlatformKeyBannerConnector'
import { RouteMetadata } from '@/design-system/atoms/RouteMetadata'
import CoreLayout from '@/design-system/organisms/Layout/CoreLayout'
import type { LayoutType } from './app/routes'
import { routes, createRouteElement } from './app/routes'
import { offlineQueueService } from './services/offline-queue.service'
import { setRedirectHandler } from './services/auth-interceptor'

// Authenticated layouts are lazy so the public routes (TRA-115) don't ship
// the admin drawer/nav or other auth-only shell code. CoreLayout stays static
// because every public route renders it immediately.
const AuthLayout = React.lazy(() => import('@/design-system/organisms/Layout/AuthLayout'))
const LessonLayout = React.lazy(() => import('@/design-system/organisms/Layout/LessonLayout'))
const AdminLayout = React.lazy(() => import('@/design-system/organisms/Layout/AdminLayout'))

/**
 * Layout component map for route layouts.
 */
const layoutComponents: Record<LayoutType, React.ComponentType<{ children: React.ReactNode }>> = {
    core: CoreLayout,
    auth: AuthLayout,
    lesson: LessonLayout,
    admin: AdminLayout,
}

function App() {
    const navigate = useNavigate()

    // Initialize offline queue service to listen for online/offline events
    useEffect(() => {
        offlineQueueService.initialize()
        return () => {
            offlineQueueService.destroy()
        }
    }, [])

    // Bridge react-router's navigate into the auth interceptor so 403/412
    // redirects soft-navigate instead of triggering a full document reload
    // (which flashes the browser's default white background in dark mode).
    useEffect(() => {
        setRedirectHandler((path) => {
            void navigate(path)
        })
        return () => {
            setRedirectHandler(null)
        }
    }, [navigate])

    return (
        <AuthProvider>
            <NotificationProvider>
                <LLMKeyProvider>
                    <UserRoleProvider>
                        <PlatformKeyStatusProvider>
                            <AuthScopedUsageQuotaProvider>
                                <PageContentWidthProvider>
                                    <AuthErrorBoundary>
                                        <RouteMetadata />
                                        <PlatformKeyBannerConnector />
                                        <Routes>
                                            {routes.map((route) => {
                                                const LayoutComponent =
                                                    layoutComponents[route.layout]
                                                return (
                                                    <Route
                                                        key={route.path}
                                                        path={route.path}
                                                        element={
                                                            <LayoutComponent>
                                                                {createRouteElement(route)}
                                                            </LayoutComponent>
                                                        }
                                                    />
                                                )
                                            })}
                                        </Routes>
                                    </AuthErrorBoundary>
                                </PageContentWidthProvider>
                            </AuthScopedUsageQuotaProvider>
                        </PlatformKeyStatusProvider>
                    </UserRoleProvider>
                </LLMKeyProvider>
            </NotificationProvider>
        </AuthProvider>
    )
}

export default App
